import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getAuth, 
  onAuthStateChanged, 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  sendPasswordResetEmail 
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/client';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState('user');
  const [loading, setLoading] = useState(true);
  const auth = getAuth();
  const navigate = useNavigate();

  async function signup(email, password, name) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update profile with display name
      if (name) {
        await updateProfile(userCredential.user, {
          displayName: name
        });
      }
      
      // Create user document in Firestore
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        name: name || '',
        email: email,
        role: 'user',
        createdAt: new Date().toISOString()
      });
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        message: error.message 
      };
    }
  }

  async function login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Fetch user role and redirect based on role
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const role = userData.role || 'user';
        setUserRole(role);
        
        // After successful login, redirect based on role
        if (role === 'admin') {
          navigate('/admin');
        } else {
          navigate('/dashboard');
        }
      }
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        message: error.message 
      };
    }
  }

  async function logout() {
    try {
      await signOut(auth);
      navigate('/');
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        message: error.message 
      };
    }
  }

  async function resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        message: error.message 
      };
    }
  }

  async function fetchUserRole(uid) {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.role || 'user';
      }
      return 'user';
    } catch (error) {
      console.error("Error fetching user role:", error);
      return 'user';
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        const role = await fetchUserRole(user.uid);
        setUserRole(role);
      } else {
        setCurrentUser(null);
        setUserRole('user');
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [auth]);

  const value = {
    currentUser,
    isAdmin: userRole === 'admin',
    userRole,
    signup,
    login,
    logout,
    resetPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 